import { RefObject, useEffect } from "react";

// below this distance a mouse drag still counts as a click
const dragThresholdInPx = 5;

/**
 * Lets the mouse pan a horizontally scrollable element whose scrollbar is hidden:
 * dragging with the pressed button, and the vertical mouse wheel.
 * Touch pans natively (with momentum), so it is left alone.
 */
export function useDragToScroll(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let dragStart: { x: number; scrollLeft: number } | undefined;
    let isDragging = false;

    function onPointerDown(e: PointerEvent) {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      dragStart = { x: e.clientX, scrollLeft: element!.scrollLeft };
      isDragging = false;
    }

    function onPointerMove(e: PointerEvent) {
      if (!dragStart) return;
      const distance = e.clientX - dragStart.x;
      if (!isDragging && Math.abs(distance) < dragThresholdInPx) return;
      if (!isDragging) {
        isDragging = true;
        element!.setPointerCapture(e.pointerId);
        element!.classList.add("dragging");
      }
      element!.scrollLeft = dragStart.scrollLeft - distance;
    }

    function onPointerUp() {
      dragStart = undefined;
      element!.classList.remove("dragging");
    }

    // a drag must not select the item it started on
    function onClickCapture(e: MouseEvent) {
      if (!isDragging) return;
      e.stopPropagation();
      e.preventDefault();
      isDragging = false;
    }

    function onWheel(e: WheelEvent) {
      const isVerticalWheel = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      const canScroll = element!.scrollWidth > element!.clientWidth;
      if (!isVerticalWheel || !canScroll) return;
      e.preventDefault();
      element!.scrollLeft += e.deltaY;
    }

    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("pointermove", onPointerMove);
    element.addEventListener("pointerup", onPointerUp);
    element.addEventListener("pointercancel", onPointerUp);
    element.addEventListener("click", onClickCapture, true);
    // not passive, so the page does not scroll along
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      element.removeEventListener("pointerdown", onPointerDown);
      element.removeEventListener("pointermove", onPointerMove);
      element.removeEventListener("pointerup", onPointerUp);
      element.removeEventListener("pointercancel", onPointerUp);
      element.removeEventListener("click", onClickCapture, true);
      element.removeEventListener("wheel", onWheel);
    };
  }, [ref]);
}
